import { CheckboxInput, Form, SelectInput, TextInput } from '@app/components'
import { showMessageNotification, usePaymentSetup } from '@app/lib'
import {
  SugarPaymentSetupLlcClassification,
  SugarPaymentSetupTaxClassificationCategory,
  SugarPaymentSetupTinType,
  SugarPaymentSetupType
} from '@app/types'
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Group,
  Paper,
  SelectItem,
  Stack,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { Dropzone, type FileWithPath, PDF_MIME_TYPE, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import { useForm, yupResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconExclamationCircle, IconExclamationMark, IconFile, IconUpload, IconX } from '@tabler/icons-react'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'

type FormData = {
  authorizationAcknowledgement: boolean
  bankAccountNumber: string
  bankAccountNumberConfirm: string
  bankAccountType: string
  bankName: string
  bankRoutingNumber: string
  bankRoutingNumberConfirm: string
  llcClassification: SugarPaymentSetupLlcClassification | null
  nameAuthorizedSigner: string
  nameOfAccount: string
  paymentSetupType: SugarPaymentSetupType
  taxAddress: {
    city: string
    postalCode: string
    state: string
    street: string
  }
  taxClassificationCategory: SugarPaymentSetupTaxClassificationCategory | null
  taxIdentificationNumber: string
  taxName: string
  tinType: SugarPaymentSetupTinType | null
  titleAuthorizedSigner: string
}

const llcClassificationOptions: SelectItem[] = [
  {
    label: 'C Corporation',
    value: SugarPaymentSetupLlcClassification.C
  },
  {
    label: 'Partnership',
    value: SugarPaymentSetupLlcClassification.P
  },
  {
    label: 'S Corporation',
    value: SugarPaymentSetupLlcClassification.S
  }
]
const taxClassificationCategoryOptions: SelectItem[] = [
  {
    label: 'C Corporation',
    value: SugarPaymentSetupTaxClassificationCategory.C
  },
  {
    label: 'Individual/Sole Proprietor/Single-member LLC',
    value: SugarPaymentSetupTaxClassificationCategory.INDIVIDUAL
  },
  {
    label: 'Limited Liability Company (LLC)',
    value: SugarPaymentSetupTaxClassificationCategory.LLC
  },
  {
    label: 'Other (including tax exempt, nonprofit, government agency)',
    value: SugarPaymentSetupTaxClassificationCategory.OTHER
  },
  {
    label: 'Partnership',
    value: SugarPaymentSetupTaxClassificationCategory.PARTNERSHIP
  },
  {
    label: 'S Corporation',
    value: SugarPaymentSetupTaxClassificationCategory.S
  },
  {
    label: 'Trust/Estate',
    value: SugarPaymentSetupTaxClassificationCategory.TRUST
  }
]
const tinTypeOptions: SelectItem[] = [
  {
    label: 'EIN',
    value: SugarPaymentSetupTinType.EIN
  },
  {
    label: 'SSN',
    value: SugarPaymentSetupTinType.SSN
  }
]

export type PaymentSetupFormProps = {
  // Lets a modal host (PaymentSetupFormModal) drive its own `isChanged` state off this form's
  // dirty state without PaymentSetupForm needing to expose its `useForm` instance. Mantine v6's
  // form.isDirty() is a function, not a reactive value, so this is recomputed every render and
  // pushed out via effect whenever it changes.
  onDirtyChange?: (isDirty: boolean) => void
  // When present, called instead of redirecting on a successful submit (the modal host case).
  // When absent, the route case redirects instead.
  onSuccess?: () => void
  paymentSetupType: SugarPaymentSetupType
}

export const PaymentSetupForm = ({ onDirtyChange, onSuccess, paymentSetupType }: PaymentSetupFormProps) => {
  const router = useRouter()
  const theme = useMantineTheme()
  const { isPending: isSettingUpPayment, mutateAsync: setupPayment } = usePaymentSetup()
  const [file, setFile] = useState<FileWithPath | null>(null)
  const form = useForm<FormData>({
    initialValues: {
      authorizationAcknowledgement: false,
      nameAuthorizedSigner: '',
      // seeded from the prop and never edited, but it MUST stay in the form values: every other
      // field's rule is gated on `.when('paymentSetupType', ...)`, and yup skips those branches
      // entirely when the key is absent from the validated object
      paymentSetupType,
      titleAuthorizedSigner: '',
      // ACH values
      bankAccountNumber: '',
      bankAccountNumberConfirm: '',
      bankAccountType: '',
      bankName: '',
      bankRoutingNumber: '',
      bankRoutingNumberConfirm: '',
      nameOfAccount: '',
      // W9 values
      llcClassification: null,
      taxAddress: {
        city: '',
        postalCode: '',
        state: '',
        street: ''
      },
      taxClassificationCategory: null,
      taxIdentificationNumber: '',
      taxName: '',
      tinType: null
    },
    validate: yupResolver(
      yup.object({
        authorizationAcknowledgement: yup.boolean().oneOf([true], 'Authorization Acknowledgement is required'),
        nameAuthorizedSigner: yup
          .string()
          .min(1, 'Must be at least one character')
          .max(255, 'Must be no longer than 255 characters')
          .required('Name of Authorized Signer is required'),
        paymentSetupType: yup
          .string()
          .oneOf([SugarPaymentSetupType.ACH, SugarPaymentSetupType.W9])
          .required('Payment Setup Type is required'),
        titleAuthorizedSigner: yup
          .string()
          .min(1, 'Must be at least one character')
          .max(255, 'Must be no longer than 255 characters')
          .required('Title of Authorized Signer is required'),

        bankAccountNumber: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) =>
            schema.matches(/^\d{1,30}$/, 'Must be between 1 and 30 characters').required('Bank Account is required')
        }),
        bankAccountNumberConfirm: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) =>
            schema
              .oneOf([yup.ref('bankAccountNumber')], 'Account Number must match')
              .required('Bank Account Confirm is required')
        }),
        bankAccountType: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) => schema.oneOf(['Checking', 'Savings']).required('Account Type is required')
        }),
        bankName: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) =>
            schema
              .min(1, 'Must be at least one character')
              .max(255, 'Must be no longer than 255 characters')
              .required('Bank Name is required')
        }),
        bankRoutingNumber: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) =>
            schema.matches(/^\d{9}$/, 'Must be exactly 9 characters').required('Routing Number is required')
        }),
        bankRoutingNumberConfirm: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) =>
            schema
              .oneOf([yup.ref('bankRoutingNumber')], 'Routing Number must match')
              .required('Routing Number Confirm is required')
        }),
        nameOfAccount: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.ACH,
          then: (schema) =>
            schema
              .min(1, 'Must be at least one character')
              .max(255, 'Must be no longer than 255 characters')
              .required('Name of Account is required')
        }),
        llcClassification: yup
          .string()
          .nullable()
          .when(['paymentSetupType', 'taxClassificationCategory'], {
            is: (
              paymentSetupType: SugarPaymentSetupType,
              taxClassificationCategory: SugarPaymentSetupTaxClassificationCategory
            ) =>
              paymentSetupType === SugarPaymentSetupType.W9 &&
              taxClassificationCategory === SugarPaymentSetupTaxClassificationCategory.LLC,
            then: (schema) =>
              schema
                .oneOf([
                  SugarPaymentSetupLlcClassification.C,
                  SugarPaymentSetupLlcClassification.P,
                  SugarPaymentSetupLlcClassification.S
                ])
                .required('LLC Classification is required')
          }),
        taxAddress: yup.object({
          city: yup.string().when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) =>
              schema
                .min(1, 'Must be at least one character')
                .max(255, 'Must be no longer than 255 characters')
                .required('City is required')
          }),
          postalCode: yup.string().when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) =>
              schema
                .min(1, 'Must be at least one character')
                .max(255, 'Must be no longer than 255 characters')
                .required('Postal Code is required')
          }),
          state: yup.string().when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) =>
              schema
                .min(1, 'Must be at least one character')
                .max(255, 'Must be no longer than 255 characters')
                .required('State is required')
          }),
          street: yup.string().when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) =>
              schema
                .min(1, 'Must be at least one character')
                .max(255, 'Must be no longer than 255 characters')
                .required('Street is required')
          })
        }),
        taxClassificationCategory: yup
          .string()
          .nullable()
          .when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) =>
              schema
                .oneOf([
                  SugarPaymentSetupTaxClassificationCategory.C,
                  SugarPaymentSetupTaxClassificationCategory.INDIVIDUAL,
                  SugarPaymentSetupTaxClassificationCategory.LLC,
                  SugarPaymentSetupTaxClassificationCategory.OTHER,
                  SugarPaymentSetupTaxClassificationCategory.PARTNERSHIP,
                  SugarPaymentSetupTaxClassificationCategory.S,
                  SugarPaymentSetupTaxClassificationCategory.TRUST
                ])
                .required('Tax Classification Category is required')
          }),
        taxIdentificationNumber: yup
          .string()
          .when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) => schema.required('Tax Identification Number is required')
          })
          .when('tinType', {
            is: SugarPaymentSetupTinType.EIN,
            then: (schema) =>
              schema.matches(/^\d{2}-?\d{7}$/, 'Must be in EIN (xx-xxxxxxx) format, with or without hyphens')
          })
          .when('tinType', {
            is: SugarPaymentSetupTinType.SSN,
            then: (schema) =>
              schema.matches(/^\d{3}-?\d{2}-?\d{4}$/, 'Must be in SSN (xxx-xx-xxxx) format, with or without hyphens')
          }),
        taxName: yup.string().when('paymentSetupType', {
          is: SugarPaymentSetupType.W9,
          then: (schema) =>
            schema
              .min(1, 'Must be at least one character')
              .max(255, 'Must be no longer than 255 characters')
              .required('Name is required')
        }),
        tinType: yup
          .string()
          .nullable()
          .when('paymentSetupType', {
            is: SugarPaymentSetupType.W9,
            then: (schema) =>
              schema
                .oneOf([SugarPaymentSetupTinType.EIN, SugarPaymentSetupTinType.SSN])
                .required('Tax Identification Number Type is required')
          })
      })
    ),
    validateInputOnChange: true
  })
  const isValid = form.isValid() && (form.values.paymentSetupType === SugarPaymentSetupType.ACH || !!file)
  // form.isDirty() deep-compares form VALUES only; the Dropzone file is separate state and is REQUIRED
  // for W-9, so without `|| !!file` a dropped upload is discarded with no confirmation.
  const isDirty = form.isDirty() || !!file

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  return (
    <Paper p='lg'>
      <Stack spacing='xl'>
        <Alert
          icon={<IconExclamationCircle size='2rem' />}
          color='orange'
        >
          Navigating away from this page, submitting a complete form, or logging out of MetrixEnroll will erase all
          information entered. We recommend having all of your account information available before proceeding.
        </Alert>
        <Form
          buttons={
            <Group position='right'>
              <Text
                color='dimmed'
                size='sm'
              >
                Once submitted, your form will be processed and erased from this page. Please double-check your account
                information!
              </Text>
              <Button
                disabled={isSettingUpPayment || !isValid}
                leftIcon={<IconCheck size={18} />}
                loading={isSettingUpPayment}
                type='submit'
              >
                Submit
              </Button>
            </Group>
          }
          isSaving={isSettingUpPayment}
          isValid={isValid}
          onSubmit={form.onSubmit(async (values) => {
            const { success, message } = await setupPayment(
              values.paymentSetupType === SugarPaymentSetupType.ACH
                ? {
                    authorizationAcknowledgement: values.authorizationAcknowledgement,
                    bankAccountNumber: values.bankAccountNumber,
                    bankAccountType: values.bankAccountType,
                    bankName: values.bankName,
                    bankRoutingNumber: values.bankRoutingNumber,
                    file: file ?? undefined,
                    nameAuthorizedSigner: values.nameAuthorizedSigner,
                    nameOfAccount: values.nameOfAccount,
                    paymentSetupType: values.paymentSetupType,
                    titleAuthorizedSigner: values.titleAuthorizedSigner
                  }
                : {
                    authorizationAcknowledgement: values.authorizationAcknowledgement,
                    file: file ?? undefined,
                    llcClassification: values.llcClassification as SugarPaymentSetupLlcClassification,
                    nameAuthorizedSigner: values.nameAuthorizedSigner,
                    paymentSetupType: values.paymentSetupType,
                    taxAddress: values.taxAddress,
                    taxClassificationCategory:
                      values.taxClassificationCategory as SugarPaymentSetupTaxClassificationCategory,
                    taxIdentificationNumber: values.taxIdentificationNumber,
                    taxName: values.taxName,
                    tinType: values.tinType as SugarPaymentSetupTinType,
                    titleAuthorizedSigner: values.titleAuthorizedSigner
                  }
            )

            if (success) {
              notifications.show({
                color: 'green',
                icon: <IconCheck />,
                message,
                withBorder: true,
                withCloseButton: false
              })

              if (onSuccess) {
                onSuccess()
              } else {
                router.push('/payment-info')
              }

              return
            }

            notifications.show({
              color: 'red',
              icon: <IconExclamationMark />,
              message,
              withBorder: true,
              withCloseButton: false
            })
          })}
        >
          {useMemo(() => {
            switch (form.values.paymentSetupType) {
              case SugarPaymentSetupType.ACH:
                return (
                  <>
                    <Text my='lg'>
                      Colorado Universal Preschool (UPK) funding will be paid by direct electronic transfer from the
                      payment vendor, MetrixIQ LLC, to the provider on behalf of the Colorado Department of Early
                      Childhood (CDEC). ACH payments will be made according to CDEC-defined payment eligibility and
                      schedule.{' '}
                      <strong>
                        By submitting the ACH Authorization Form, the provider authorizes MetrixIQ to initiate credit
                        (deposit) transactions and correcting debit (withdrawal) transactions to the designated account
                        for UPK.
                      </strong>{' '}
                      Providers will receive notification of ACH transactions via email. To change or cancel
                      authorization, the provider must provide a written request to{' '}
                      <a href='mailto:coupkpayments@metrixiq.com'>coupkpayments@metrixiq.com</a>. Please note: MetrixIQ
                      will process a $0 transaction to verify your banking information. Please ensure all information
                      provided is correct to ensure timely payments.
                    </Text>
                    <Title
                      order={4}
                      my='lg'
                    >
                      Please provide your banking information for the account you wish to receive Universal Preschool
                      payments via ACH (direct deposit):
                    </Title>
                    <TextInput
                      disabled={isSettingUpPayment}
                      label='Name of Account'
                      required
                      {...form.getInputProps('nameOfAccount')}
                    />
                    <Group
                      grow
                      spacing='xl'
                    >
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Bank Name'
                        required
                        {...form.getInputProps('bankName')}
                      />
                      <SelectInput
                        disabled={isSettingUpPayment}
                        data={['Checking', 'Savings']}
                        label='Account Type'
                        required
                        {...form.getInputProps('bankAccountType')}
                      />
                    </Group>
                    <Group
                      grow
                      spacing='xl'
                    >
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Routing Number'
                        required
                        {...form.getInputProps('bankRoutingNumber')}
                      />
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Account Number'
                        required
                        {...form.getInputProps('bankAccountNumber')}
                      />
                    </Group>
                    <Title
                      my='lg'
                      order={4}
                    >
                      Confirm your banking information:
                    </Title>
                    <Group
                      grow
                      spacing='xl'
                    >
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Routing Number'
                        required
                        {...form.getInputProps('bankRoutingNumberConfirm')}
                      />
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Account Number'
                        required
                        {...form.getInputProps('bankAccountNumberConfirm')}
                      />
                    </Group>
                    <Title
                      my='lg'
                      order={4}
                    >
                      Upload a voided check or official bank letter for the account provided (optional):
                    </Title>
                  </>
                )
              case SugarPaymentSetupType.W9:
                return (
                  <>
                    <Text my='lg'>
                      <strong>
                        All providers must submit a valid Form W-9, even if your organization is tax-exempt.
                      </strong>{' '}
                      UPK funding is taxable and it is the provider responsibility to report the income to the IRS.
                      Taxable entities will receive a Form 1099-MISC from MetrixIQ by January 31st annually. Please
                      note: MetrixIQ will verify the provided tax information with the IRS. Please ensure the provided
                      information exactly matches your business information as on file with the IRS (e.g., a previous
                      tax return). You cannot receive UPK payments if invalid information is submitted.
                    </Text>
                    <Anchor
                      href='https://www.irs.gov/pub/irs-pdf/fw9.pdf'
                      rel='noopener noreferrer'
                      size='xs'
                      target='_blank'
                    >
                      Download Blank W9 Form
                    </Anchor>
                    <Title
                      order={4}
                      my='lg'
                    >
                      Please provide your tax information:
                    </Title>
                    <TextInput
                      disabled={isSettingUpPayment}
                      label='Tax Business Name'
                      required
                      {...form.getInputProps('taxName')}
                    />
                    <Group
                      grow
                      spacing='xl'
                    >
                      <SelectInput
                        disabled={isSettingUpPayment}
                        data={tinTypeOptions}
                        label='TIN Type'
                        required
                        {...form.getInputProps('tinType')}
                      />
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Tax Identification Number (TIN)'
                        required
                        {...form.getInputProps('taxIdentificationNumber')}
                      />
                    </Group>
                    <Group
                      grow
                      spacing='xl'
                    >
                      <SelectInput
                        disabled={isSettingUpPayment}
                        data={taxClassificationCategoryOptions}
                        label='Federal Tax Classification'
                        required
                        {...form.getInputProps('taxClassificationCategory')}
                      />
                      {form.values.taxClassificationCategory === SugarPaymentSetupTaxClassificationCategory.LLC && (
                        <SelectInput
                          disabled={isSettingUpPayment}
                          data={llcClassificationOptions}
                          label='LLC Category'
                          required
                          {...form.getInputProps('llcClassification')}
                        />
                      )}
                    </Group>
                    <Title
                      my='lg'
                      order={5}
                    >
                      Tax Address:
                    </Title>
                    <TextInput
                      disabled={isSettingUpPayment}
                      label='Street'
                      required
                      {...form.getInputProps('taxAddress.street')}
                    />
                    <Group
                      grow
                      spacing='xl'
                    >
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='City'
                        required
                        {...form.getInputProps('taxAddress.city')}
                      />
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='State'
                        required
                        {...form.getInputProps('taxAddress.state')}
                      />
                    </Group>
                    <Group
                      grow
                      spacing='xl'
                    >
                      <TextInput
                        disabled={isSettingUpPayment}
                        label='Postal Code'
                        required
                        {...form.getInputProps('taxAddress.postalCode')}
                      />
                    </Group>
                    <Title
                      my='lg'
                      order={4}
                    >
                      Upload a completed W9 form (required): <span style={{ color: 'red' }}>*</span>
                    </Title>
                  </>
                )
            }
          }, [form, isSettingUpPayment])}
          <Dropzone
            accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
            disabled={!!file}
            maxSize={30_000_000}
            multiple={false}
            onDrop={(dropped) => setFile(dropped[0])}
            onReject={(fileRejections) => {
              if (fileRejections[0].errors[0].code === 'file-too-large') {
                showMessageNotification({
                  message: 'Max file size is 30mb!',
                  type: 'warning'
                })
              }
            }}
          >
            <Group
              position='center'
              spacing='xl'
              style={{ minHeight: 200, pointerEvents: 'none' }}
            >
              <Dropzone.Accept>
                <IconUpload
                  size='3.2rem'
                  stroke={1.5}
                  color={theme.colors[theme.primaryColor][6]}
                />
                <Text>Drop a valid file here, or click to select a file.</Text>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  size='3.2rem'
                  stroke={1.5}
                  color={theme.colors.red[6]}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFile
                  size='3.2rem'
                  stroke={1.5}
                />
              </Dropzone.Idle>
              <Text>Drop the valid file here, or click to select a file.</Text>
            </Group>
          </Dropzone>
          {file && (
            <Group
              noWrap
              position='apart'
            >
              <Text
                fw='bold'
                size='sm'
              >
                {file.name}
              </Text>
              <ActionIcon onClick={() => setFile(null)}>
                <IconX
                  color='red'
                  size={18}
                />
              </ActionIcon>
            </Group>
          )}
          <Title
            my='lg'
            order={3}
          >
            Authorization Acknowledgement
          </Title>
          <Text
            fw='bold'
            my='lg'
          >
            Checking the box and typing your name below constitutes the same authority as a signature. Please
            double-check the account information entered above before proceeding.
          </Text>
          <CheckboxInput
            disabled={isSettingUpPayment}
            label='By checking here, I certify that I am an authorized person to request Universal Preschool payments to the provided account. I certify that I have read, understand, and agree with the authorization statements above. I certify that the information provided is true and correct to the best of my knowledge.'
            my='lg'
            required
            styles={(theme) => ({
              label: {
                fontSize: theme.fontSizes.md
              }
            })}
            {...form.getInputProps('authorizationAcknowledgement', { type: 'checkbox' })}
          />
          <Group
            grow
            spacing='xl'
          >
            <TextInput
              disabled={isSettingUpPayment}
              label='Name of Authorized Signer'
              required
              {...form.getInputProps('nameAuthorizedSigner')}
            />
            <TextInput
              disabled={isSettingUpPayment}
              label='Title of Authorized Signer'
              required
              {...form.getInputProps('titleAuthorizedSigner')}
            />
          </Group>
        </Form>
      </Stack>
    </Paper>
  )
}
